import React, { useState } from "react";

// Edit mode: every field is a read-only snapshot of the record except
// Bill Amount and Bill Number, which the vendor is allowed to update.
const EDIT_FIELDS = [
  { label: "Bill Amount", api_name: "Bill_Amount", data_type: "Currency", icon: "₹", readOnly: false },
  { label: "Month", api_name: "Month", icon: "▦", source: "Month", readOnly: true },
  { label: "Year", api_name: "Year", icon: "▦", source: "Year", readOnly: true },
  { label: "Bill Number", api_name: "Bill_Number", data_type: "Single Line", icon: "#", readOnly: false },
];

const BILL_LINE_COLUMNS = [
  { key: "Location", label: "Location" },
  { key: "Bill_Salary", label: "Bill Salary" },
  { key: "Commission", label: "Commission" },
  { key: "GST", label: "GST" },
  { key: "Amount", label: "Amount" },
];

const formatCurrency = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CREATE_FIELDS = [
  { label: "Location", api_name: "Location", data_type: "Single Line", icon: "◎" },
  { label: "People Id", api_name: "People_Id", data_type: "Single Line", icon: "#" },
  { label: "People Bill Owner", api_name: "Owner", data_type: "Lookup", icon: "#" },
  { label: "Article Vendor", api_name: "Article_Vendor", data_type: "Lookup", icon: "#" },
  { label: "Bill Amount", api_name: "Bill_Amount", data_type: "Currency", icon: "₹" },
  { label: "Commission", api_name: "Commission", data_type: "Percent", icon: "%" },
  { label: "Bill GST", api_name: "Bill_GST", data_type: "Currency", icon: "₹" },
  { label: "Amount", api_name: "Amount", data_type: "Currency", icon: "₹" },
  { label: "Month", api_name: "Month", data_type: "Single Line", icon: "▦" },
  { label: "Year", api_name: "Year", data_type: "Single Line", icon: "▦" },
  { label: "Bill Number", api_name: "Bill_Number", data_type: "Single Line", icon: "#" },
];

export default function NewBill({ onCancel, onSuccess, editData, vendorInfo }) {
  const isEdit = !!editData;
  const fields = isEdit ? EDIT_FIELDS : CREATE_FIELDS;

  const [formData, setFormData] = useState({
    Location: editData?.Location || "",
    People_Id: editData?.People_Id || "",
    Owner: editData?.Owner?.id || "",
    Article_Vendor: isEdit ? (editData?.Article_Vendor?.id || "") : (vendorInfo?.id || ""),
    Bill_Amount: editData?.Bill_Amount ?? "",
    Commission: editData?.Commission ?? "",
    Bill_GST: editData?.Bill_GST ?? "",
    Amount: editData?.Amount ?? "",
    Month: editData?.Month || "",
    Year: editData?.Year || "",
    Bill_Number: editData?.Bill_Number || "",
  });
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasExistingAttachment =
    Array.isArray(editData?.Attachment) && editData.Attachment.length > 0;
  const billLines = Array.isArray(editData?.Bills) ? editData.Bills : [];
  const grandTotal = editData?.Grand_Total;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      fields
        .filter((field) => !field.readOnly)
        .forEach((field) => {
          const val = formData[field.api_name];
          if (val === "" || val === null || val === undefined) return;
          formDataToSend.append(field.api_name, val);
        });
      if (file) {
        formDataToSend.append("Attachment", file);
      }

      console.log(`${isEdit ? "Updating" : "Submitting"} form data...`);

      const url = isEdit
        ? `/server/dealer_backend/update-people-bill/${editData.id}`
        : '/server/dealer_backend/create-people-bill';

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: formDataToSend,
      });

      const result = await response.json();
      if (result.status === "success" && result.data?.[0]?.code === "SUCCESS") {
        alert(isEdit ? "Bill updated successfully!" : "Bill created successfully!");
        onSuccess();
      } else {
        const errorDetails = result.data?.[0]?.details || result.error || "Failed to process request";
        console.error("Zoho CRM Error:", result);
        alert(`Error: ${JSON.stringify(errorDetails)}`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? "updating" : "creating"} bill:`, error);
      alert(`An error occurred while ${isEdit ? "updating" : "creating"} the bill. Check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-bill-page">
      <div className="form-breadcrumb">
        <span className="breadcrumb-link">Home</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-link">People Bills</span>
        <span className="breadcrumb-sep">›</span>
        <span className="breadcrumb-current">{isEdit ? "Edit Record" : "New Record"}</span>
      </div>

      <p className="page-eyebrow">{isEdit ? "MODIFICATION" : "CREATION"}</p>

      <div className="form-header">
        <button type="button" className="btn-back" onClick={onCancel} aria-label="Back">←</button>
        <div>
          <h2 className="form-title">{isEdit ? "Edit People Bill" : "Create New Bill"}</h2>
          <p className="form-subtitle">
            {isEdit ? `Updating bill record for ${editData.Name}` : "Add a new record to the bill directory"}
          </p>
        </div>
      </div>

      <form className="bill-form" onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-card-head">
            <span className="form-card-title">
              <span className="form-card-icon">▤</span>
              RECORD INFORMATION
            </span>
            {isEdit && (
              <span className="form-note-badge">
                Note: Only specific fields are editable in update mode.
              </span>
            )}
          </div>

          <div className="form-grid">
            {fields.map((field) => (
              <div key={field.label} className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">{field.icon}</span>
                  {field.label}
                </label>
                {field.readOnly ? (
                  <input
                    type="text"
                    className="form-input form-input--readonly"
                    value={editData?.[field.source] ?? ""}
                    placeholder="—"
                    readOnly
                    disabled
                  />
                ) : (
                  <input
                    type={field.data_type === "Currency" || field.data_type === "Percent" ? "number" : "text"}
                    className="form-input"
                    name={field.api_name}
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                    value={formData[field.api_name]}
                    onChange={handleChange}
                    step={field.data_type === "Currency" || field.data_type === "Percent" ? "0.01" : undefined}
                  />
                )}
                {field.data_type === "Lookup" && !isEdit && (
                  <small className="form-hint">Enter the ID for this record</small>
                )}
              </div>
            ))}
          </div>
        </div>

        {isEdit && (
          <div className="form-card">
            <div className="form-card-head">
              <span className="form-card-title">
                <span className="form-card-icon">▤</span>
                BILLS
              </span>
              {grandTotal !== null && grandTotal !== undefined && (
                <div className="form-total-badge">
                  <span className="form-total-label">Grand Total</span>
                  <span className="form-total-value">{formatCurrency(grandTotal)}</span>
                </div>
              )}
            </div>

            {billLines.length === 0 ? (
              <p className="td-empty">No line items found for this bill.</p>
            ) : (
              <div className="table-wrapper">
                <table className="bill-table subform-table">
                  <thead>
                    <tr>
                      {BILL_LINE_COLUMNS.map((col) => (
                        <th key={col.key} className="th">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {billLines.map((line) => (
                      <tr key={line.id} className="tr">
                        <td className="td">{line.Location || "—"}</td>
                        <td className="td">{formatCurrency(line.Bill_Salary)}</td>
                        <td className="td">{formatCurrency(line.Commission)}</td>
                        <td className="td">{formatCurrency(line.GST)}</td>
                        <td className="td">{formatCurrency(line.Amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="form-card">
          <div className="form-card-head">
            <span className="form-card-title">
              <span className="form-card-icon">📎</span>
              SUPPORTING DOCUMENTS
            </span>
          </div>

          <label
            className={`dropzone ${dragActive ? "dropzone--active" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <input
              type="file"
              className="dropzone-input"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <span className="dropzone-icon">⭱</span>
            <p className="dropzone-title">
              {file ? file.name : "Drop your bill attachment here"}
            </p>
            <p className="dropzone-hint">
              {file
                ? "Click or drop to replace"
                : hasExistingAttachment
                ? "A file is already attached — drop a new one to replace it"
                : "PDF, PNG, JPG up to 10MB"}
            </p>
          </label>
        </div>

        <div className="form-actions form-actions--sticky">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {isEdit ? "Discard Changes" : "Cancel"}
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            <span className="btn-primary-icon">✓</span>
            {loading ? (isEdit ? "Updating..." : "Creating...") : isEdit ? "Update Record" : "Save Bill"}
          </button>
        </div>
      </form>
    </div>
  );
}
